import { Fragment } from 'react';
import { Button, cn } from '@herit-fe/heritage-ui';
import type { MarketItem, PlantContract, RevenueDisplayType } from '@/shared/data-types';
import {
  formatInteger,
  getFixedUnitPrice,
  marketDeltaText,
  marketDeltaTone,
  marketMonthDay,
  marketPriceText,
  SUFFIX,
} from '@/shared/lib';
import { UI_TEXT } from '@/shared/languages';

const EMPTY = UI_TEXT.COMMON_EMPTY_VALUE;

/**
 * 시장가 Footer — SMP·REC 를 [현재가][전일대비][기준일] 3 컬럼으로 세로 정렬. 단일 grid 로 두 행의 컬럼 경계를
 * 공유(auto 폭이 두 행 중 넓은 셀 기준) → 값 폭이 달라도 SMP·REC 가 어긋나지 않음. 구앱 MarketFooter 이식
 * (기준일=MM.DD, 등락 hot/cold=`deltaTone`). 값 자릿수·단위는 `marketPriceText`(SMP 소수 2자리 / REC 정수 + '원').
 */
function MarketFooter({ items }: { items: MarketItem[] }) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-3 gap-y-2 text-sm tabular-nums">
      {items.map((item) => {
        const label = item.kind === 'smp' ? UI_TEXT.REVENUE_MARKET_SMP : UI_TEXT.REVENUE_MARKET_REC;
        return (
          <Fragment key={item.kind}>
            <span className="text-muted-foreground">{label}</span>
            <span className="text-right font-semibold text-foreground">
              {marketPriceText(item)}
            </span>
            <span className={cn('text-right', marketDeltaTone(item.delta))}>
              {marketDeltaText(item)}
            </span>
            <span className="text-right text-xs text-muted-foreground">
              {marketMonthDay(item.date)}
            </span>
          </Fragment>
        );
      })}
    </div>
  );
}

function ContractFooter({
  displayType,
  contract,
  onInput,
}: {
  displayType: RevenueDisplayType;
  contract: PlantContract | null;
  onInput: () => void;
}) {
  if (displayType === 'SELF_NOT_SET') {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{UI_TEXT.REVENUE_CONTRACT_TITLE}</p>
          <p className="text-xs text-muted-foreground">{UI_TEXT.REVENUE_CONTRACT_SELF_NOT_SET}</p>
        </div>
        <Button variant="outline" size="sm" className="shrink-0" onClick={onInput}>
          {UI_TEXT.REVENUE_CONTRACT_INPUT}
        </Button>
      </div>
    );
  }
  if (displayType === 'SELF_SET') {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">{UI_TEXT.REVENUE_CONTRACT_TITLE}</p>
          <p className="text-xs text-muted-foreground">{UI_TEXT.REVENUE_CONTRACT_SELF_SET}</p>
        </div>
        {contract?.contractName ? (
          <span className="shrink-0 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            {contract.contractName}
          </span>
        ) : null}
      </div>
    );
  }
  // SALE_FIXED_SET — 계약방식 + 고정단가.
  const priceLabel =
    contract?.contractCode === 'P101'
      ? UI_TEXT.REVENUE_CONTRACT_PRICE_P101
      : UI_TEXT.REVENUE_CONTRACT_PRICE_P103;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{UI_TEXT.TERM_CONTRACT_METHOD}</span>
        <span className="text-sm font-medium text-foreground">
          {contract?.contractName ?? EMPTY}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">{priceLabel}</span>
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {formatInteger(getFixedUnitPrice(contract), SUFFIX.WON)}
        </span>
      </div>
    </div>
  );
}

/**
 * 수익 Footer — 전체/발전사업(미설정·현물) = 시장가(SMP/REC 전일대비) / 자가소비·고정단가 = 계약 정보.
 * `displayType` null = 전체. 구앱 `getFooterSlot` + `Card.Footer` 이식 — 카드 하단 footer 영역(상단 divider +
 * subtle 톤, 자체 테두리·radius 없음 → 카드 border/모서리 공유). 표시할 내용 없으면 영역 자체를 안 그림.
 * 수익 리포트·대시보드 SINGLE 이 공유(계약 기반 Footer SSOT) → shared/ui.
 */
export function RevenueFooter({
  displayType,
  marketItems,
  contract,
  onContractInput,
}: {
  displayType: RevenueDisplayType | null;
  marketItems: MarketItem[];
  contract: PlantContract | null;
  onContractInput: () => void;
}) {
  const content =
    displayType === 'SELF_NOT_SET' ||
    displayType === 'SELF_SET' ||
    displayType === 'SALE_FIXED_SET' ? (
      <ContractFooter displayType={displayType} contract={contract} onInput={onContractInput} />
    ) : marketItems.length > 0 ? (
      // 전체(null) / NOT_REGISTERED / SALE_NOT_SET / SALE_SET → 시장가.
      <MarketFooter items={marketItems} />
    ) : null;

  if (!content) return null;
  return <div className="border-t border-border bg-muted/40 px-4 py-3">{content}</div>;
}
